import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    CaretLeft, DotsThreeVertical, Info, MagnifyingGlass, Fire, Spinner, Tray,
    Archive, ArrowClockwise, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import ConversationItem from "@/components/ConversationItem";
import Thread from "@/components/Thread";
import Composer from "@/components/Composer";
import ConversationDetails from "@/components/ConversationDetails";
import SofiPanel from "@/components/SofiPanel";
import KimKimTripLink from "@/components/KimKimTripLink";
import MobileMenuDrawer, { MobileMenuTrigger } from "@/components/mobile/MobileMenuDrawer";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Single-pane mobile inbox — Phase 8.
 *
 * Renders one of THREE views based on selection + URL:
 *
 *   Level 1: `/inbox` (no conversation selected)
 *     Conversation list, full width, hamburger menu (opens drawer),
 *     search bar and archived-filter tabs pinned under the header.
 *
 *   Level 2: `/inbox?conversation_id=X` (selected, view != details)
 *     Sticky header with back button + trip title + info button +
 *     overflow menu (archive/unarchive). Thread scrolls. Composer
 *     sticky at the bottom, safe-area padded.
 *
 *   Level 3: `/inbox?conversation_id=X&view=details`
 *     SOFI panel + conversation details, full screen. Back returns to
 *     the thread view (Level 2), preserving the selected conversation.
 *
 * Uses the browser back button natively — every level transition flips a
 * search-param, so hardware Back / swipe Back on iOS Chrome / etc. work
 * without any custom logic.
 *
 * All the state lives up in `InboxPage.jsx` — this component is a pure
 * layout / navigation shell. Callbacks are passed in.
 */
export default function InboxMobile(props) {
    const {
        user, users, usersById,
        conversations, filteredConversations,
        selectedId, setSelectedId, selectedDetail,
        loadingList, loadingDetail,
        searchQ, setSearchQ,
        archivedFilter, setArchivedFilter,
        outboundState,
        reloadList,
        onSendMessage, onAddNote, patchConv,
        setArchiveOpen,
        wsStatus,
        onOpenDeleteMessage,
    } = props;

    const [searchParams, setSearchParams] = useSearchParams();
    const [menuOpen, setMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    // Level 3 gate — mirrored in the URL so hardware back button works.
    const showDetails = searchParams.get("view") === "details";
    const setShowDetails = useCallback((v) => {
        const next = new URLSearchParams(searchParams);
        if (v) next.set("view", "details"); else next.delete("view");
        // Use `replace: true` when hiding details so the L3 URL doesn't
        // linger in history — otherwise a user mixing in-app back with the
        // browser back button could re-surface L3 after they thought they
        // returned to L2. When SHOWING details we push normally so a native
        // back genuinely returns to the thread.
        setSearchParams(next, { replace: !v });
    }, [searchParams, setSearchParams]);

    // Guard: if the user goes to a detail URL for a conv they haven't
    // selected (deeplink), clear the flag so we don't show an empty details
    // screen. Effectively pops back to the list.
    useEffect(() => {
        if (showDetails && !selectedId) {
            setShowDetails(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDetails, selectedId]);

    const conv = selectedDetail?.conversation;
    const headerCustomer = selectedDetail?.customer;
    const headerTrip = selectedDetail?.trip;
    const isViewer = user?.role === "viewer";

    // -------------------- LEVEL 1: LIST --------------------
    if (!selectedId) {
        return (
            <div className="flex-1 flex flex-col min-h-0 bg-white" data-testid="mobile-inbox-level1">
                {/* Sticky top strip */}
                <div className="vh-safe-top border-b border-slate-200 bg-white sticky top-0 z-30 shrink-0">
                    <div className="px-3 py-2 flex items-center gap-2">
                        <MobileMenuTrigger onClick={() => setMenuOpen(true)} />
                        <div className="flex-1 min-w-0">
                            {searchOpen ? (
                                <div className="relative">
                                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                    <input
                                        autoFocus
                                        type="search"
                                        value={searchQ}
                                        onChange={(e) => setSearchQ(e.target.value)}
                                        placeholder="Buscar por cliente…"
                                        className="w-full pl-9 pr-9 py-2 text-base bg-slate-50 border border-slate-200 rounded-md focus:bg-white focus:border-brand-400 focus:ring-1 focus:ring-brand-200 focus:outline-none"
                                        data-testid="mobile-search-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => { setSearchQ(""); setSearchOpen(false); }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-slate-400 hover:bg-slate-200"
                                        aria-label="Cerrar búsqueda"
                                    >
                                        <X size={14} weight="bold" />
                                    </button>
                                </div>
                            ) : (
                                <div className="font-display font-bold text-lg text-slate-900 tracking-tight">Inbox</div>
                            )}
                        </div>
                        {!searchOpen && (
                            <button
                                type="button"
                                onClick={() => setSearchOpen(true)}
                                className="vh-tappable rounded-md text-slate-700 hover:bg-slate-100 flex items-center justify-center"
                                aria-label="Buscar"
                                data-testid="mobile-search-open"
                            >
                                <MagnifyingGlass size={20} weight="regular" />
                            </button>
                        )}
                    </div>
                    {/* Archived filter tabs — always visible */}
                    <div className="px-3 pb-2 flex items-center gap-1.5 overflow-x-auto" data-testid="mobile-archived-filter">
                        {[
                            { id: "false", label: "Activas" },
                            { id: "true",  label: "Archivadas" },
                            { id: "all",   label: "Todas" },
                        ].map(opt => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => setArchivedFilter(opt.id)}
                                className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                                    archivedFilter === opt.id
                                        ? "bg-brand-600 border-brand-600 text-white"
                                        : "bg-white border-slate-200 text-slate-600 active:bg-slate-100"
                                }`}
                                data-testid={`mobile-filter-${opt.id}`}
                                aria-pressed={archivedFilter === opt.id}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Conversation list */}
                <div className="flex-1 overflow-y-auto vh-mobile-scroll" data-testid="mobile-conversation-list">
                    {loadingList && (
                        <div className="p-8 flex items-center justify-center text-slate-400">
                            <Spinner className="animate-spin mr-2" /> Cargando…
                        </div>
                    )}
                    {!loadingList && filteredConversations.length === 0 && (
                        <div className="p-10 flex flex-col items-center text-slate-400 text-sm gap-2 text-center">
                            {searchQ ? <MagnifyingGlass size={32} /> : <Tray size={32} />}
                            <span>{searchQ ? `Sin coincidencias para «${searchQ}»` : "No hay conversaciones"}</span>
                        </div>
                    )}
                    {filteredConversations.map(c => (
                        <ConversationItem
                            key={c.id}
                            conv={c}
                            active={false}
                            onClick={() => setSelectedId(c.id)}
                            currentUserId={user?.id}
                            usersById={usersById}
                        />
                    ))}
                </div>

                <MobileMenuDrawer open={menuOpen} onOpenChange={setMenuOpen} wsStatus={wsStatus} />
            </div>
        );
    }

    // -------------------- LEVEL 3: DETAILS --------------------
    if (showDetails) {
        return (
            <div className="flex-1 flex flex-col min-h-0 bg-white" data-testid="mobile-inbox-level3">
                {/* Sticky top strip with back button */}
                <div className="vh-safe-top border-b border-slate-200 bg-white sticky top-0 z-30 shrink-0">
                    <div className="px-2 py-2 flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setShowDetails(false)}
                            className="vh-tappable rounded-md text-slate-700 hover:bg-slate-100 flex items-center justify-center"
                            aria-label="Volver al chat"
                            data-testid="mobile-details-back"
                        >
                            <CaretLeft size={22} weight="regular" />
                        </button>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-base text-slate-900 tracking-tight truncate">
                                Detalles del trip
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                                {headerTrip?.title || headerCustomer?.name || "—"}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto vh-mobile-scroll" data-testid="mobile-details-scroll">
                    {conv && (
                        <>
                            {/* Fase 9.3.1 mobile parity — enlace directo al chat de KimKim.
                                Se muestra ANTES que SofiPanel para que el operador
                                lo tenga a mano incluso cuando SOFI no está matched. */}
                            {headerTrip && (headerTrip.external_refs?.kimkim_trip_slug
                                || headerTrip.external_refs?.kimkim_trip_slug_original_case) && (
                                <div className="px-4 pt-4 pb-2 border-b border-slate-100">
                                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">
                                        KimKim · Chat original
                                    </div>
                                    <KimKimTripLink trip={headerTrip} />
                                </div>
                            )}
                            <div className="px-4 pt-4 pb-2 border-b border-slate-100">
                                <SofiPanel conversation={conv} customer={headerCustomer} />
                            </div>
                            <ConversationDetails
                                conversation={conv}
                                trip={headerTrip}
                                customer={headerCustomer}
                                users={users}
                                currentUser={user}
                                onChangeStatus={(s) => patchConv({ status: s })}
                                onChangeAssignee={(uid) => patchConv({ assigned_user_id: uid })}
                                onToggleUrgent={() => patchConv({ urgency: conv.urgency === "urgent" ? "normal" : "urgent" })}
                            />
                        </>
                    )}
                </div>
            </div>
        );
    }

    // -------------------- LEVEL 2: THREAD --------------------
    return (
        <div className="flex-1 flex flex-col min-h-0 bg-white" data-testid="mobile-inbox-level2">
            {/* Sticky top strip with back / title / info / overflow */}
            <div className="vh-safe-top border-b border-slate-200 bg-white sticky top-0 z-30 shrink-0">
                <div className="px-2 py-2 flex items-center gap-1 min-w-0">
                    <button
                        type="button"
                        onClick={() => setSelectedId(null)}
                        className="vh-tappable rounded-md text-slate-700 hover:bg-slate-100 flex items-center justify-center shrink-0"
                        aria-label="Volver al inbox"
                        data-testid="mobile-thread-back"
                    >
                        <CaretLeft size={22} weight="regular" />
                    </button>

                    {loadingDetail && !conv && (
                        <div className="flex-1 flex items-center text-slate-500 text-sm">
                            <Spinner className="animate-spin mr-2" /> Cargando…
                        </div>
                    )}

                    {conv && (
                        <>
                            <button
                                type="button"
                                onClick={() => setShowDetails(true)}
                                className="flex-1 min-w-0 flex items-center gap-2 px-1.5 py-1 rounded-md active:bg-slate-100 text-left"
                                data-testid="mobile-thread-title-btn"
                            >
                                <Avatar name={headerCustomer?.name} size={34} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-display font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                        <span className="truncate">
                                            {(headerTrip?.title || headerTrip?.external_refs?.kimkim_trip_slug || headerCustomer?.name || "—")}
                                        </span>
                                        {conv.urgency === "urgent" && (
                                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1 py-0.5 rounded shrink-0">
                                                <Fire size={9} weight="fill"/>
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">
                                        {headerCustomer?.name || "—"}
                                        {conv.channel && (
                                            <>
                                                <span className="mx-1">·</span>
                                                <span className="uppercase tracking-wider font-semibold">
                                                    {conv.channel === "kimkim" ? "KimKim" : conv.channel === "manual" ? "Manual" : conv.channel}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowDetails(true)}
                                className="vh-tappable rounded-md text-slate-700 hover:bg-slate-100 flex items-center justify-center shrink-0"
                                aria-label="Ver detalles del trip"
                                data-testid="mobile-thread-info-btn"
                            >
                                <Info size={20} weight="regular" />
                            </button>

                            {user?.role === "admin" && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            className="vh-tappable rounded-md text-slate-700 hover:bg-slate-100 flex items-center justify-center shrink-0"
                                            aria-label="Más opciones"
                                            data-testid="mobile-thread-overflow"
                                        >
                                            <DotsThreeVertical size={22} weight="bold" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="min-w-[220px]">
                                        {conv.archived ? (
                                            <DropdownMenuItem
                                                onClick={async () => {
                                                    try {
                                                        await api.post(`/conversations/${conv.id}/unarchive`);
                                                        toast.success("Desarchivada, vuelve al inbox activo");
                                                        reloadList();
                                                    } catch (e) {
                                                        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo desarchivar");
                                                    }
                                                }}
                                                data-testid="mobile-thread-unarchive"
                                            >
                                                <ArrowClockwise size={18} className="mr-2 text-emerald-600" />
                                                <span>Desarchivar</span>
                                            </DropdownMenuItem>
                                        ) : (
                                            <DropdownMenuItem
                                                onClick={() => setArchiveOpen(true)}
                                                data-testid="mobile-thread-archive"
                                            >
                                                <Archive size={18} className="mr-2 text-slate-600" />
                                                <span>Archivar (solo ViajaHub)</span>
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Thread — flex-1 so the composer stays pinned. */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {loadingDetail && !conv && (
                    <div className="flex-1 flex items-center justify-center text-slate-400">
                        <Spinner className="animate-spin mr-2" /> Cargando conversación…
                    </div>
                )}
                {conv && (
                    <>
                        <Thread
                            messages={selectedDetail.messages}
                            notes={selectedDetail.notes}
                            customer={headerCustomer}
                            usersById={usersById}
                            currentUserId={user?.id}
                            onDeleteMessage={onOpenDeleteMessage}
                        />

                        <Composer
                            onSendMessage={onSendMessage}
                            onAddNote={onAddNote}
                            disabled={isViewer || conv.status === "closed"}
                            sendDisabled={
                                conv.channel === "kimkim" &&
                                (!conv.outbound_allowed || outboundState.paused)
                            }
                            sendDisabledReason={
                                conv.channel === "kimkim" && outboundState.paused
                                    ? "Envíos a clientes pausados por el admin (kill-switch). Reanúdalos en /admin/channels."
                                    : (conv.channel === "kimkim" && !conv.outbound_allowed
                                        ? "⚠️ Solo lectura — este trip no está autorizado para envíos."
                                        : undefined)
                            }
                            sendDisabledSeverity={
                                conv.channel === "kimkim" && !conv.outbound_allowed && !outboundState.paused
                                    ? "critical"
                                    : "warning"
                            }
                            requireConfirm={false}
                        />

                        {conv.is_poc_phase && outboundState.outbound_mode !== "all_kimkim" && (
                            <div className="text-center py-1.5 text-[11px] bg-amber-50 border-t border-amber-200 text-amber-800 vh-safe-bottom">
                                Modo POC · whitelist = <code>th1DXPSB</code>
                            </div>
                        )}
                        {conv.status === "closed" && (
                            <div className="text-center py-1.5 text-[11px] text-slate-500 bg-slate-100 border-t border-slate-200 vh-safe-bottom">
                                Conversación cerrada.
                            </div>
                        )}
                        {isViewer && (
                            <div className="text-center py-1.5 text-[11px] text-slate-500 bg-slate-100 border-t border-slate-200 vh-safe-bottom">
                                Modo solo lectura.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
